import { Children, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { Popup, useControllableValue, useEscapeKey, useOnClickOutside } from '@/ui/kit'
import { cn } from './internal/utils'
import { BORDER, RADIUS_CONTROL, RADIUS_PANEL, STATUS_FIELD } from './internal/presets'
import Spin from './Spin'
import Empty from './Empty'

const Option = ({ children }) => children
const OptGroup = ({ children }) => children

/** Accepts either `options` or `<Select.Option>` children, both used in this app. */
const normalizeOptions = (options, children) => {
  if (options?.length) {
    return options.flatMap((option) =>
      option.options
        ? option.options.map((child) => ({ ...child, group: option.label }))
        : [option]
    )
  }

  return Children.toArray(children)
    .filter(Boolean)
    .flatMap((child) => {
      if (child.type === OptGroup) {
        return Children.toArray(child.props.children).map((grandChild) => ({
          value: grandChild.props.value,
          label: grandChild.props.children ?? grandChild.props.label,
          disabled: grandChild.props.disabled,
          group: child.props.label,
        }))
      }
      return [
        {
          value: child.props.value,
          label:
            typeof child.props.label === 'string' || typeof child.props.label === 'number'
              ? String(child.props.label)
              : child.props.children ?? child.props.label,
          node: child.props.children,
          disabled: child.props.disabled,
        },
      ]
    })
}

const toText = (label) => {
  if (label == null) return ''
  if (typeof label === 'string' || typeof label === 'number') return String(label)
  return ''
}

const defaultFilter = (input, option) =>
  toText(option.label).toLowerCase().includes(input.toLowerCase()) ||
  String(option.value).toLowerCase().includes(input.toLowerCase())

/**
 * antd Select. Covers single and `multiple`/`tags` modes, `showSearch` with
 * `filterOption`, `allowClear`, `loading`, `disabled`, `status`, `labelInValue`
 * and the `options`/`Option` children APIs.
 */
const Select = ({
  value,
  defaultValue,
  onChange,
  onSelect,
  onDeselect,
  onSearch,
  onClear,
  onBlur,
  onFocus,
  onDropdownVisibleChange,
  options,
  children,
  mode,
  placeholder,
  showSearch = false,
  filterOption,
  optionFilterProp,
  allowClear = false,
  disabled = false,
  loading = false,
  status,
  size = 'middle',
  maxTagCount,
  labelInValue = false,
  notFoundContent,
  dropdownRender,
  dropdownStyle,
  popupMatchSelectWidth = true,
  fieldNames,
  className,
  style,
  id,
  'aria-label': ariaLabel,
  ...rest
}) => {
  const isMultiple = mode === 'multiple' || mode === 'tags'
  const [selected, setSelected] = useControllableValue(value, defaultValue ?? (isMultiple ? [] : undefined))
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t('common.select')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const anchorRef = useRef(null)
  const popupRef = useRef(null)
  const searchInputRef = useRef(null)

  const normalized = useMemo(() => {
    const resolved = normalizeOptions(options, children)
    if (!fieldNames) return resolved
    return resolved.map((option) => ({
      ...option,
      value: option[fieldNames.value] ?? option.value,
      label: option[fieldNames.label] ?? option.label,
    }))
  }, [children, fieldNames, options])

  const filtered = useMemo(() => {
    if (!showSearch || !search) return normalized
    if (filterOption === false) return normalized
    const predicate =
      typeof filterOption === 'function'
        ? filterOption
        : optionFilterProp
          ? (input, option) => toText(option[optionFilterProp]).toLowerCase().includes(input.toLowerCase())
          : defaultFilter
    return normalized.filter((option) => predicate(search, option))
  }, [filterOption, normalized, optionFilterProp, search, showSearch])

  const setOpenState = (next) => {
    setOpen(next)
    onDropdownVisibleChange?.(next)
    if (!next) setSearch('')
  }

  useOnClickOutside([anchorRef, popupRef], () => setOpenState(false), open)
  useEscapeKey(() => setOpenState(false), open)

  const rawValues = isMultiple ? (selected ?? []) : selected
  const selectedValues = isMultiple
    ? rawValues.map((item) => (labelInValue && item?.value !== undefined ? item.value : item))
    : labelInValue && rawValues?.value !== undefined
      ? rawValues.value
      : rawValues

  const optionOf = (candidate) =>
    normalized.find((option) => String(option.value) === String(candidate))

  const emit = (nextValue, option) => {
    const payload = labelInValue
      ? Array.isArray(nextValue)
        ? nextValue.map((item) => ({ value: item, label: toText(optionOf(item)?.label) }))
        : { value: nextValue, label: toText(option?.label) }
      : nextValue
    setSelected(payload)
    onChange?.(payload, option)
  }

  const focusSearchInput = () => {
    requestAnimationFrame(() => {
      searchInputRef.current?.focus()
      window.setTimeout(() => searchInputRef.current?.focus(), 0)
    })
  }

  useEffect(() => {
    if (!open || !showSearch) return
    focusSearchInput()
  }, [open, showSearch])

  const pick = (option) => {
    if (option.disabled) return
    if (isMultiple) {
      const exists = selectedValues.some((item) => String(item) === String(option.value))
      const next = exists
        ? selectedValues.filter((item) => String(item) !== String(option.value))
        : [...selectedValues, option.value]
      emit(next, option)
      if (exists) onDeselect?.(option.value, option)
      else onSelect?.(option.value, option)
      setSearch('')
      if (showSearch) focusSearchInput()
      return
    }
    emit(option.value, option)
    onSelect?.(option.value, option)
    setOpenState(false)
  }

  const clear = (event) => {
    event.stopPropagation()
    emit(isMultiple ? [] : undefined, undefined)
    onClear?.()
  }

  const heightClass =
    size === 'small'
      ? 'min-h-[var(--control-height-sm)]'
      : 'min-h-[var(--control-height)]'
  const hasValue = isMultiple ? selectedValues.length > 0 : selectedValues != null && selectedValues !== ''

  const visibleTags = maxTagCount != null ? selectedValues.slice(0, maxTagCount) : selectedValues
  const hiddenTagCount = isMultiple ? selectedValues.length - (visibleTags?.length ?? 0) : 0

  return (
    <div
      className={cn(
        'ui-select relative',
        isMultiple ? 'ui-select-multiple' : 'ui-select-single',
        open && 'ui-select-open',
        disabled && 'ui-select-disabled',
        status === 'error' && 'ui-select-status-error',
        className
      )}
      style={style}
    >
      <div
        ref={anchorRef}
        id={id}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        aria-invalid={status === 'error' || undefined}
        onClick={() => !disabled && setOpenState(!open)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={(event) => {
          if (disabled) return
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
            event.preventDefault()
            setOpenState(true)
          }
        }}
        className={cn(
          'ui-select-selector ui-input-base flex w-full min-h-11 cursor-pointer items-center gap-2 px-3 py-1.5',
          RADIUS_CONTROL,
          STATUS_FIELD[status],
          heightClass,
          disabled && 'ui-input-disabled cursor-not-allowed'
        )}
        {...rest}
      >
        {showSearch ? (
          <Search
            size={16}
            aria-hidden="true"
            className="shrink-0 text-[var(--muted-foreground)]"
          />
        ) : null}

        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {isMultiple && hasValue
            ? [
                ...visibleTags.map((item) => (
                  <span
                    key={item}
                    className="ui-select-selection-item inline-flex max-w-full items-center gap-1.5 rounded-full border border-[var(--color-primary-200)] bg-[var(--color-primary-50)] px-2.5 py-1 text-xs font-medium text-[var(--color-primary-700)]"
                  >
                    <span className="max-w-[14rem] truncate">
                      {toText(optionOf(item)?.label) || String(item)}
                    </span>
                    <button
                      type="button"
                      aria-label={`Retirer ${toText(optionOf(item)?.label) || item}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.stopPropagation()
                        pick({ value: item })
                        if (showSearch) focusSearchInput()
                      }}
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-[var(--color-primary-100)]"
                    >
                      <X size={12} aria-hidden="true" />
                    </button>
                  </span>
                )),
                hiddenTagCount > 0 ? (
                  <span key="__rest__" className="text-xs text-[var(--text-muted)]">
                    +{hiddenTagCount}
                  </span>
                ) : null,
              ]
            : null}

          {!isMultiple && hasValue && !(showSearch && open) ? (
            <span className="ui-select-selection-item truncate text-sm text-[var(--text-dark)]">
              {toText(optionOf(selectedValues)?.label) || String(selectedValues)}
            </span>
          ) : null}

          {showSearch && open ? (
            <input
              ref={searchInputRef}
              autoFocus
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                onSearch?.(event.target.value)
              }}
              onClick={(event) => event.stopPropagation()}
              placeholder={hasValue && isMultiple ? '' : resolvedPlaceholder}
              aria-label={t('common.search')}
              className="ui-select-selection-search-input min-w-[8rem] flex-1 border-0 bg-transparent p-0 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)]"
            />
          ) : null}

          {!hasValue && !(showSearch && open) ? (
            <span className="ui-select-selection-placeholder truncate text-sm text-[var(--muted-foreground)]">
              {resolvedPlaceholder}
            </span>
          ) : null}
        </span>

        {loading ? <Spin size="small" /> : null}
        {allowClear && hasValue && !disabled ? (
          <button
            type="button"
            aria-label={t('common.clearSelection')}
            onClick={clear}
            className="ui-select-clear inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[var(--gray-600)] hover:text-[var(--text-body)]"
          >
            <X size={12} aria-hidden="true" />
          </button>
        ) : null}
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={cn(
            'ui-select-arrow shrink-0 text-[var(--gray-600)] transition-transform',
            open && 'rotate-180'
          )}
        />
      </div>

      <Popup
        anchorRef={anchorRef}
        popupRef={popupRef}
        open={open}
        placement="bottomLeft"
        matchAnchorWidth={popupMatchSelectWidth !== false}
        className={cn(
          'ui-select-dropdown max-h-64 overflow-auto border bg-[var(--surface-card)] py-1 shadow-[var(--shadow-md)]',
          BORDER,
          RADIUS_PANEL
        )}
        style={dropdownStyle}
      >
        {dropdownRender ? (
          dropdownRender(
            <SelectMenu options={filtered} selectedValues={selectedValues} isMultiple={isMultiple} onPick={pick} notFoundContent={notFoundContent} />
          )
        ) : (
          <SelectMenu
            options={filtered}
            selectedValues={selectedValues}
            isMultiple={isMultiple}
            onPick={pick}
            notFoundContent={notFoundContent}
          />
        )}
      </Popup>
    </div>
  )
}

const SelectMenu = ({ options, selectedValues, isMultiple, onPick, notFoundContent }) => {
  const { t } = useTranslation()
  if (options.length === 0) {
    return (
      <div className="ui-select-item-empty px-2 py-3">
        {notFoundContent ?? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('common.noResults')} />}
      </div>
    )
  }

  let lastGroup

  return (
    <ul role="listbox" aria-multiselectable={isMultiple || undefined} className="m-0 list-none p-0">
      {options.map((option) => {
        const isSelected = isMultiple
          ? selectedValues.some((item) => String(item) === String(option.value))
          : String(selectedValues) === String(option.value)
        const groupHeader = option.group && option.group !== lastGroup ? option.group : null
        lastGroup = option.group

        return (
          <li key={String(option.value)} role="none">
            {groupHeader ? (
              <div className="ui-select-item-group px-3 py-1 text-xs font-semibold uppercase text-[var(--text-muted)]">
                {groupHeader}
              </div>
            ) : null}
            <div
              role="option"
              aria-selected={isSelected}
              aria-disabled={option.disabled || undefined}
              tabIndex={-1}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onPick(option)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onPick(option)
              }}
              className={cn(
                'ui-select-item ui-select-item-option flex min-h-11 cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm',
                option.disabled
                  ? 'cursor-not-allowed text-[var(--gray-500)]'
                  : 'hover:bg-[var(--muted)]',
                isSelected &&
                  'ui-select-item-option-selected bg-[var(--color-primary-50)] font-medium text-[var(--color-primary-700)]'
              )}
            >
              <span className={cn('min-w-0 flex-1', !option.node && 'truncate')}>
                {option.node ?? option.label ?? String(option.value)}
              </span>
              {isSelected ? <Check size={14} aria-hidden="true" className="shrink-0" /> : null}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

Select.Option = Option
Select.OptGroup = OptGroup

export { OptGroup, Option }
export default Select
